"use client"

import { useState, useEffect, useMemo, useCallback } from "react"
import { ProtectedRoute } from "@/components/protected-route"
import { Sidebar } from "@/components/sidebar"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { AdminPageSkeleton } from "@/components/ui/skeleton"
import { useAuth } from "@/context/auth-context"
import { getAsesores, getAnalytics, supabase, normalizeStatus, getCurrentCampus, getAllCampus, countLeadChildren } from "@/lib/supabase"
import { ChartPie, Users, Calendar, TrendingUp, Baby, Megaphone, Download } from "@/lib/icons"
import { toast } from "sonner"
import { generateMonthlyReport } from "@/lib/monthly-report"
import { formatPercentage, formatNumber } from "@/lib/utils"
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  RadialLinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Filler,
  Tooltip,
  Legend,
} from "chart.js"

// Importar componentes modulares
import { DashboardFilters } from "./components/DashboardFilters"
import { MetricsCharts } from "./components/MetricsCharts"
import { TeamView } from "./components/TeamView"
import { AdvisorProfileModal } from "./components/AdvisorProfileModal"
import { CampaignsView } from "./components/CampaignsView"
import { MonthSelector } from "./components/MonthSelector"
import { PeriodBar } from "./components/PeriodBar"
import {
  STATUS_ORDER,
  STATUS_LABELS,
  STATUS_COLORS,
  FOLLOW_UP_STATUSES,
  COMPLETED_STATUSES,
  DROPOUT_STATUSES,
} from "./components/constants"

ChartJS.register(CategoryScale, LinearScale, RadialLinearScale, PointElement, LineElement, BarElement, ArcElement, Tooltip, Legend, Filler)

// Fallback Estático de Asesores (Copiado de LoginPage para consistencia)
const STATIC_ADVISORS = [
  { email: "admin@tepeyac.mx", rol: "admin", nombre: "Administrador", campus: "Guadalajara" },
  { email: "alejandro.rojas@tepeyac.edu.mx", rol: "asesor", nombre: "Alejandro Rojas", campus: "MDS Campus Puebla" },
  { email: "carla.ruiz@tepeyac.edu.mx", rol: "asesor", nombre: "Carla Ruiz", campus: "Instituto Tepeyac Queretaro" },
  { email: "carlos.jimenez@tepeyac.edu.mx", rol: "asesor", nombre: "Carlos Jimenez", campus: "Instituto Tepeyac Xcaret" },
  { email: "daniela.gomez@tepeyac.edu.mx", rol: "asesor", nombre: "Daniela Gomez", campus: "IT Innovation School Toluca" },
  { email: "fernanda.perez@tepeyac.edu.mx", rol: "asesor", nombre: "Fernanda Perez", campus: "IT Innovation School Coacalco" },
  { email: "jorge.martinez@tepeyac.edu.mx", rol: "asesor", nombre: "Jorge Martinez", campus: "Instituto Tepeyac Leon" },
  { email: "laura.mendez@tepeyac.edu.mx", rol: "asesor", nombre: "Laura Mendez", campus: "IT Innovation School Cancun" },
  { email: "luis.hernandez@tepeyac.edu.mx", rol: "asesor", nombre: "Luis Hernandez", campus: "IT Innovation School Aguascalientes" },
  { email: "ricardo.lopez@tepeyac.edu.mx", rol: "asesor", nombre: "Ricardo Lopez", campus: "IT Innovation School Cuautitlan" },
  { email: "yair@tepeyac.mx", rol: "asesor", nombre: "Yair Hernandez", campus: "IT Innovation School Guadalajara" },
]

// ============================================================
// COMPONENTE PRINCIPAL
// ============================================================

export const AdminPage = () => {
  // Estados de UI
  const [isDarkMode, setIsDarkMode] = useState(false)
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [selectedAdvisor, setSelectedAdvisor] = useState(null)
  const [activeTab, setActiveTab] = useState("overview")

  // Estados de datos
  // Estados de datos
  const [analytics, setAnalytics] = useState(null)
  const [asesores, setAsesores] = useState([])
  const [allCampuses, setAllCampuses] = useState([]) // Lista oficial de campuses
  const [totalNinos, setTotalNinos] = useState(0)

  // Estados de filtros
  const currentCampus = typeof getCurrentCampus === "function" ? getCurrentCampus() : (getCurrentCampus ?? null)
  // Default: IT Innovation School Guadalajara (primer campus activo)
  const ACTIVE_CAMPUS_NAME = "IT Innovation School Guadalajara"
  const defaultCampus = (currentCampus && currentCampus !== "default") ? currentCampus : ACTIVE_CAMPUS_NAME
  const [campusSelected, setCampusSelected] = useState(defaultCampus)
  const [citySelected, setCitySelected] = useState("Todos")
  const [showAllAdvisors, setShowAllAdvisors] = useState(false)
  // Mes seleccionado (default: mes actual). Filtra todas las métricas del dashboard.
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date()
    return new Date(now.getFullYear(), now.getMonth(), 1)
  })

  const { user } = useAuth()

  // Detectar modo oscuro
  useEffect(() => {
    if (typeof window === "undefined") return
    const root = document.documentElement
    const update = () => setIsDarkMode(root.classList.contains("dark"))
    update()
    const observer = new MutationObserver(update)
    observer.observe(root, { attributes: true, attributeFilter: ["class"] })
    return () => observer.disconnect()
  }, [])

  // Verificar si es admin
  const OWNER_EMAILS = new Set(["admin@tepeyac.mx", "ramiro@tepeyact.com"])
  const isAdminUser = Boolean(
    user?.isAdmin || user?.rol === "admin" || OWNER_EMAILS.has(String(user?.email || "").toLowerCase())
  )
  const adminDisplayName = useMemo(
    () => user?.nombre ?? user?.name ?? user?.email ?? "Administrador",
    [user]
  )

  // ============================================================
  // FETCH DATA
  // ============================================================

  const loadData = useCallback(async () => {
    setLoading(true)
    try {
      const [analyticsResult, asesoresResult, campusResult, ninosCount] = await Promise.all([
        getAnalytics(),
        getAsesores(),
        getAllCampus({ includeInactive: true }), // Fetch oficial de todos los campus (activos e inactivos)
        countLeadChildren(),
      ])

      if (analyticsResult.data) {
        setAnalytics(analyticsResult.data)
      }
      if (asesoresResult.data) {
        setAsesores(asesoresResult.data)
      }
      if (campusResult.data) {
        setAllCampuses(campusResult.data)
      }
      setTotalNinos(ninosCount || 0)
    } catch (error) {
      console.error("Error loading data:", error)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadData()
  }, [loadData])

  // Suscripción a cambios en tiempo real (leads)
  useEffect(() => {
    const channel = supabase
      .channel("admin-dashboard-leads")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "leads" },
        (payload) => {
          // console.log("Real-time update received:", payload)
          loadData() // Recargar datos al recibir cualquier cambio
        }
      )
      .subscribe()

    return () => {
      supabase.removeChannel(channel)
    }
  }, [loadData])

  // ============================================================
  // DATOS DERIVADOS & NORMALIZACION
  // ============================================================

  // Mapa de respaldo estático para garantizar resolución de nombres
  // (Basado en la información provista por el usuario)
  const STATIC_CAMPUS_MAP = {
    "1a13d2ba-f926-4b77-bbe1-09b51f2f0c90": "IT Innovation School Guadalajara",
    "ee9693a3-1193-445a-bf60-a0d2bcbd8fca": "Instituto Tepeyac Queretaro",
    "aa2afd84-4664-4f01-92a6-118221d9649d": "Instituto Tepeyac Xcaret",
    "2e45227a-e099-48d3-893b-9bc633bfb808": "IT Innovation School Toluca",
    "62ecdb9f-9b02-4f8c-a093-aed48e3a4185": "IT Innovation School Coacalco",
    "c8ead6da-5638-4d4d-b2da-dd2cf108d8f1": "Instituto Tepeyac Leon",
    "ce483f59-0279-4ddc-a1b4-392875d9e45f": "IT Innovation School Cancun",
    "572e9464-2b53-4727-9d83-f21dd14d5f3e": "IT Innovation School Aguascalientes",
    "47ca0982-d394-4f35-8eb1-6417aa4167fa": "IT Innovation School Cuautitlan",
    "853f60a3-d371-4978-a1f8-ec0a09f82fce": "MDS Campus Puebla",
  }

  // Mapa de ID -> Nombre de Campus para búsqueda rápida
  const campusMap = useMemo(() => {
    const map = new Map()

    // 1. Cargar mapa estático primero (prioridad base)
    Object.entries(STATIC_CAMPUS_MAP).forEach(([id, name]) => {
      map.set(id, name)
    })

    // 2. Sobreescribir/Complementar con datos de DB
    allCampuses.forEach(c => {
      if (c.id) map.set(c.id, c.nombre)
      if (c.codigo) map.set(c.codigo, c.nombre)
    })
    return map
  }, [allCampuses])

  // Normalización de leads para asegurar que 'campus' sea siempre el nombre legible
  const standardizedLeads = useMemo(() => {
    if (!analytics?.leads) return []

    return analytics.leads.map(lead => {
      // Intentar resolver el nombre del campus
      let campusName = "Sin campus"

      // 1. Si tenemos el objeto campus join
      if (lead.campus?.nombre) {
        campusName = lead.campus.nombre
      }
      // 2. Si tenemos campus_id, buscar en el mapa de allCampuses
      else if (lead.campus_id && campusMap.has(lead.campus_id)) {
        campusName = campusMap.get(lead.campus_id)
      }
      // 3. Si 'campus' es un string (a veces es ID, a veces nombre)
      else if (typeof lead.campus === 'string') {
        const val = lead.campus.trim()
        // Si parece UUID y está en el mapa
        if (campusMap.has(val)) {
          campusName = campusMap.get(val)
        } else {
          // Asumir que es el nombre si no es UUID
          campusName = val
        }
      }

      return {
        ...lead,
        _campusName: campusName, // Campo normalizado interno
        _municipio: lead.municipio || lead.campus_municipio || lead.ciudad || "Sin ciudad"
      }
    })
  }, [analytics, campusMap])

  // Lista de campus para el filtro (basado en catálogo oficial + lo que haya en leads)
  const campusFilterOptions = useMemo(() => {
    const options = new Set(["Todos"])

    // Agregar del catálogo oficial
    allCampuses.forEach(c => options.add(c.nombre))

    // Agregar de leads por si hay alguno huerfano
    standardizedLeads.forEach(l => {
      if (l._campusName && l._campusName !== "Sin campus") {
        options.add(l._campusName)
      }
    })

    return Array.from(options).sort()
  }, [allCampuses, standardizedLeads])

  // Lista de ciudades
  const cities = useMemo(() => {
    const options = new Set(["Todos"])
    standardizedLeads.forEach(l => {
      if (l._municipio && l._municipio !== "Sin ciudad") {
        options.add(l._municipio)
      }
    })
    // También agregar municipios de los campus oficiales
    allCampuses.forEach(c => {
      if (c.municipio) options.add(c.municipio)
    })

    return Array.from(options).sort()
  }, [standardizedLeads, allCampuses])

  const campusSelectValue = useMemo(
    () => (campusFilterOptions.includes(campusSelected) ? campusSelected : "Todos"),
    [campusFilterOptions, campusSelected]
  )

  const citySelectValue = useMemo(
    () => (cities.includes(citySelected) ? citySelected : "Todos"),
    [cities, citySelected]
  )

  // Leads filtrados (incluye filtro por mes seleccionado)
  const leads = useMemo(() => {
    let list = [...standardizedLeads]

    // Filtro por mes seleccionado — solo leads creados dentro de ese mes
    const monthStart = new Date(selectedMonth.getFullYear(), selectedMonth.getMonth(), 1)
    const monthEnd = new Date(selectedMonth.getFullYear(), selectedMonth.getMonth() + 1, 1)
    list = list.filter((l) => {
      const created = l.created_at ? new Date(l.created_at) : null
      if (!created || isNaN(created.getTime())) return false
      return created >= monthStart && created < monthEnd
    })

    if (campusSelected && campusSelected !== "Todos") {
      list = list.filter(l => l._campusName === campusSelected)
    }

    if (citySelected && citySelected !== "Todos") {
      list = list.filter(l =>
        String(l._municipio).toLowerCase() === String(citySelected).toLowerCase()
      )
    }

    return list
  }, [standardizedLeads, campusSelected, citySelected, selectedMonth])


  // Resumen de métricas
  const summary = useMemo(() => {
    if (!leads || !leads.length) {
      return { total: 0, completed: 0, followUp: 0, conversionRate: 0, followUpRate: 0, todayNew: 0 }
    }
    let completed = 0,
      followUp = 0
    const today = new Date().toDateString()

    leads.forEach((lead) => {
      const status = normalizeStatus(lead.estado_lead)
      if (FOLLOW_UP_STATUSES.has(status)) followUp += 1
      else if (COMPLETED_STATUSES.has(status)) completed += 1
    })

    const todayNew = leads.filter((l) => {
      const created = new Date(l.created_at)
      return created.toDateString() === today
    }).length

    return {
      total: leads.length,
      completed,
      followUp,
      conversionRate: leads.length > 0 ? (completed / leads.length) * 100 : 0,
      followUpRate: leads.length > 0 ? (followUp / leads.length) * 100 : 0,
      todayNew,
    }
  }, [leads])

  // Conteo de inscritos DEL MES SELECCIONADO por fecha de inscripción
  // (no por created_at del lead: un lead de abril inscrito en junio cuenta en junio)
  const enrolledCount = useMemo(() => {
    const monthStart = new Date(selectedMonth.getFullYear(), selectedMonth.getMonth(), 1)
    const monthEnd = new Date(selectedMonth.getFullYear(), selectedMonth.getMonth() + 1, 1)
    return (standardizedLeads || []).filter((l) => {
      const status = normalizeStatus(l.estado_lead)
      if (status !== "inscrito" && status !== "cierre_inscripcion") return false
      const raw = l.inscripcion_confirmada_at || l.fecha_inscripcion || l.updated_at || l.created_at
      const d = raw ? new Date(raw) : null
      return d && !isNaN(d.getTime()) && d >= monthStart && d < monthEnd
    }).length
  }, [standardizedLeads, selectedMonth])

  // Leads por estado
  const leadsByStatus = useMemo(() => {
    const counts = {}
      ; (leads || []).forEach((l) => {
        const st = normalizeStatus(l.estado_lead)
        counts[st] = (counts[st] || 0) + 1
      })

    // Mapear TODOS los estados del STATUS_ORDER, incluso si tienen 0
    const result = STATUS_ORDER.map((status) => ({
      key: status,
      name: STATUS_LABELS[status] ?? status,
      count: counts[status] ?? 0,
      color: STATUS_COLORS[status] ?? "#6366f1",
    }))

    return result
  }, [leads])

  // Leads por nivel educativo
  const leadsByLevel = useMemo(() => {
    if (!leads) return []
    const map = {}
      ; (leads || []).forEach((l) => {
        const level = l.nivel_educativo || "Sin nivel"
        map[level] = (map[level] || 0) + 1
      })
    return Object.entries(map)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count)
  }, [leads])

  // Estadísticas del equipo
  const teamStats = useMemo(() => {
    if (!leads || !asesores) {
      return { leads: 0, inscritos: 0, follow: 0, active: 0, desertados: 0, conversion: 0, followRate: 0, count: 0, byStatus: {} }
    }

    let inscritos = 0,
      follow = 0,
      desertados = 0
    const byStatus = {}

    leads.forEach((lead) => {
      const status = normalizeStatus(lead.estado_lead)
      byStatus[status] = (byStatus[status] || 0) + 1
      if (COMPLETED_STATUSES.has(status)) inscritos += 1
      else if (FOLLOW_UP_STATUSES.has(status)) follow += 1
      else if (DROPOUT_STATUSES.has(status)) desertados += 1
    })

    const active = leads.length - inscritos - desertados

    return {
      leads: leads.length,
      inscritos,
      follow,
      active,
      desertados,
      conversion: leads.length > 0 ? (inscritos / leads.length) * 100 : 0,
      followRate: leads.length > 0 ? (follow / leads.length) * 100 : 0,
      count: asesores.length,
      byStatus,
    }
  }, [leads, asesores])

  // Función para normalizar campus de un asesor
  const normalizeAdvisorCampus = (asesor) => {
    // Intentar obtener el campus de diferentes campos posibles
    const campusValue = asesor?.campus ?? asesor?.campus_id ?? asesor?.sede ?? ""
    return String(campusValue).trim() || "Sin campus"
  }

  // Ranking de asesores (todos)
  const allRankedAdvisors = useMemo(() => {
    if (!asesores || !leads) return []

    const normalizeEmail = (val) => String(val ?? "").trim().toLowerCase()

    return asesores
      .map((asesor) => {
        const asesorLeads = leads.filter(
          (l) => normalizeEmail(l.asesor_email) === normalizeEmail(asesor.email)
        )
        const totalLeads = asesorLeads.length
        const inscritos = asesorLeads.filter((l) =>
          COMPLETED_STATUSES.has(normalizeStatus(l.estado_lead))
        ).length
        const conversionRate = totalLeads > 0 ? (inscritos / totalLeads) * 100 : 0

        // Normalizar el campus del asesor usando todas las fuentes posibles
        let resolvedCampus = normalizeAdvisorCampus(asesor)
        let resolvedName = asesor.nombre

        // Buscar match estático por email
        const staticMatch = STATIC_ADVISORS.find(sa => normalizeEmail(sa.email) === normalizeEmail(asesor.email))

        // 1. Corregir Nombre si hace falta
        if (!resolvedName || resolvedName.includes("-") || resolvedName.length > 30) {
          if (staticMatch) resolvedName = staticMatch.nombre
        }

        // 2. Corregir Campus (si es UUID o genérico)
        const looksLikeId = resolvedCampus.length > 30 && resolvedCampus.includes("-")
        const isGeneric = !resolvedCampus || resolvedCampus === "Sin campus"

        if (looksLikeId || isGeneric) {
          // Prioridad: Estático -> Mapa Dinámico
          if (staticMatch && staticMatch.campus) {
            resolvedCampus = staticMatch.campus
          } else if (campusMap.has(resolvedCampus)) {
            resolvedCampus = campusMap.get(resolvedCampus)
          }
        }

        return {
          ...asesor,
          nombre: resolvedName || asesor.email || "Asesor",
          campus: resolvedCampus,
          assignedLeads: asesorLeads,
          totalLeads,
          inscritos,
          conversionRate,
        }
      })
      .sort((a, b) => b.inscritos - a.inscritos)
  }, [asesores, leads, campusMap])

  // Asesores filtrados por campus (si hay uno seleccionado)
  // Solo mostrar asesores activos del equipo principal
  const ACTIVE_ADVISOR_EMAILS = new Set([
    "admin@tepeyac.mx",
    "director@tepeyac.edu.mx",
    "yair@tepeyac.mx",
    "gaby@tepeyac.mx",
    "lulu@tepeyac.mx",
  ])

  const rankedAdvisors = useMemo(() => {
    const filtered = allRankedAdvisors.filter((asesor) => {
      const email = String(asesor.email || "").toLowerCase().trim()
      return ACTIVE_ADVISOR_EMAILS.has(email)
    })

    if (!showAllAdvisors && campusSelected !== "Todos") {
      return filtered.filter((asesor) => {
        const asesorCampus = String(asesor.campus || "").toLowerCase().trim()
        const selectedCampus = String(campusSelected).toLowerCase().trim()
        return asesorCampus === selectedCampus
      })
    }

    return filtered
  }, [allRankedAdvisors, campusSelected, showAllAdvisors])

  // Insights del equipo
  const teamInsightCards = useMemo(() => {
    const topAdvisor = rankedAdvisors[0]
    return [
      {
        label: "Total Leads",
        value: formatNumber(teamStats.leads),
        helper: "En seguimiento activo",
        accent: "from-sky-50 to-blue-50",
        icon: Users,
      },
      {
        label: "Niños (ventas reales)",
        value: formatNumber(totalNinos),
        helper: teamStats.leads > 0 ? `${(totalNinos / Math.max(teamStats.leads, 1)).toFixed(1)} por prospecto` : "",
        accent: "from-rose-50 to-pink-50",
        icon: Baby,
      },
      {
        label: "Inscritos",
        value: formatNumber(teamStats.inscritos),
        helper: `${formatPercentage(teamStats.conversion)} conversión`,
        accent: "from-emerald-50 to-teal-50",
        icon: TrendingUp,
      },
      {
        label: "En Seguimiento",
        value: formatNumber(teamStats.follow),
        helper: `${formatPercentage(teamStats.followRate)} del total`,
        accent: "from-amber-50 to-orange-50",
        icon: Calendar,
      },
      {
        label: "Top Asesor",
        value: topAdvisor?.nombre || "N/A",
        helper: topAdvisor ? `${topAdvisor.inscritos} inscritos` : "",
        accent: "from-violet-50 to-purple-50",
        icon: Users,
        action: topAdvisor ? () => setSelectedAdvisor(topAdvisor) : undefined,
      },
    ]
  }, [teamStats, rankedAdvisors, totalNinos])

  // Resumen de ubicación
  const teamLocationSummary = useMemo(() => {
    if (citySelectValue !== "Todos") {
      return { label: `Ciudad: ${citySelectValue}` }
    }
    if (campusSelectValue !== "Todos") {
      return { label: `Campus: ${campusSelectValue}` }
    }
    return { label: "Todos los campus" }
  }, [campusSelectValue, citySelectValue])

  const locationTotals = useMemo(() => {
    return {
      total: leads.length,
      inscritos: teamStats.inscritos,
      conversionRate: teamStats.conversion,
    }
  }, [leads, teamStats])

  // (Tarjetas "Esta Semana / Este Mes / Total Histórico" eliminadas 2026-06-09:
  // redundantes con el filtro de mes + KPIs del header — pedido de Yair)

  // ============================================================
  // FUNCIONES
  // ============================================================



  const handleChangeCampus = useCallback((value) => {
    setCampusSelected(value)
  }, [])

  const handleRefresh = useCallback(() => {
    setRefreshing(true)
    loadData().finally(() => setRefreshing(false))
  }, [loadData])

  // ============================================================
  // RENDERIZADO
  // ============================================================

  if (loading) {
    return (
      <ProtectedRoute requireAdmin={true}>
        {() => <AdminPageSkeleton />}
      </ProtectedRoute>
    )
  }

  return (
    <ProtectedRoute requireAdmin={true}>
      {({ onLogout }) => (
        <Sidebar onLogout={onLogout}>
          <div className="min-h-full bg-gradient-to-br from-slate-50 via-white to-rose-50/30 dark:from-slate-950 dark:via-slate-900 dark:to-rose-950/20 pb-safe">
            <div className="container-fluid mx-auto px-4 py-6 max-w-[1800px]">
              {/* Filtros y Métricas Principales */}
              <DashboardFilters
                adminDisplayName={adminDisplayName}
                summary={summary}
                enrolledCount={enrolledCount}
                totalNinos={totalNinos}
                refreshing={refreshing}
                onRefresh={handleRefresh}
              />

              {/* Tabs de navegación */}
              <Tabs defaultValue="overview" className="space-y-4">
                <TabsList className="w-full grid grid-cols-3 rounded-2xl bg-slate-100/80 dark:bg-slate-800/80 p-1 sm:p-1.5 backdrop-blur-sm shadow-inner">
                  <TabsTrigger
                    value="overview"
                    className="flex items-center justify-center gap-1.5 sm:gap-2 rounded-xl px-2 py-2.5 sm:py-3 text-xs sm:text-sm font-display font-semibold transition-all data-[state=active]:bg-white data-[state=active]:shadow-lg data-[state=active]:text-primary dark:data-[state=active]:bg-slate-700 dark:data-[state=active]:text-white"
                  >
                    <ChartPie className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                    <span className="hidden xs:inline">Vista</span> General
                  </TabsTrigger>
                  <TabsTrigger
                    value="campaigns"
                    className="flex items-center justify-center gap-1.5 sm:gap-2 rounded-xl px-2 py-2.5 sm:py-3 text-xs sm:text-sm font-display font-semibold transition-all data-[state=active]:bg-white data-[state=active]:shadow-lg data-[state=active]:text-primary dark:data-[state=active]:bg-slate-700 dark:data-[state=active]:text-white"
                  >
                    <Megaphone className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                    Campañas
                  </TabsTrigger>
                  <TabsTrigger
                    value="team"
                    className="flex items-center justify-center gap-1.5 sm:gap-2 rounded-xl px-2 py-2.5 sm:py-3 text-xs sm:text-sm font-display font-semibold transition-all data-[state=active]:bg-white data-[state=active]:shadow-lg data-[state=active]:text-primary dark:data-[state=active]:bg-slate-700 dark:data-[state=active]:text-white"
                  >
                    <Users className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                    <span className="hidden xs:inline">Mi</span> Equipo
                  </TabsTrigger>
                </TabsList>

                {/* Barra de período + reporte mensual */}
                <PeriodBar
                  selectedMonth={selectedMonth}
                  onMonthChange={setSelectedMonth}
                >
                  <button
                    type="button"
                    onClick={() => {
                      try {
                        const r = generateMonthlyReport(standardizedLeads, selectedMonth)
                        toast.success(`Reporte descargado: ${r.cohorte} prospectos · ${r.inscritos} inscritos`)
                      } catch (e) {
                        console.error("[reporte]", e)
                        toast.error("No se pudo generar el reporte")
                      }
                    }}
                    className="inline-flex items-center gap-1.5 rounded-xl bg-gradient-to-br from-rose-700 to-rose-900 px-3 py-2 text-xs font-bold text-white shadow-md shadow-rose-900/20 hover:shadow-lg hover:-translate-y-0.5 transition-all"
                    title="Descarga el reporte ejecutivo del mes seleccionado (Excel): resumen por canal, campañas, inscritos y etapas"
                  >
                    <Download className="h-3.5 w-3.5" />
                    Reporte del mes
                  </button>
                </PeriodBar>

                <TabsContent value="overview" className="space-y-5">
                  {/* Gráficas a ancho completo: ¿en qué etapa? ¿qué nivel? ¿cuándo? ¿quién se inscribió? */}
                  <MetricsCharts
                    key={`metrics-${activeTab}`}
                    leadsByStatus={leadsByStatus}
                    leadsByLevel={leadsByLevel}
                    summary={summary}
                    isDarkMode={isDarkMode}
                    leads={leads}
                  />

                </TabsContent>

                {/* Campañas: ¿de dónde vienen y qué campaña los trae? */}
                <TabsContent value="campaigns">
                  <CampaignsView leads={leads} />
                </TabsContent>

                {/* Vista Equipo */}
                <TabsContent value="team">
                  <TeamView
                    key={`team-${activeTab}`}
                    rankedAdvisors={rankedAdvisors}
                    onSelectAdvisor={setSelectedAdvisor}
                  />
                </TabsContent>
              </Tabs>
            </div>
          </div>

          {/* Modal de perfil del asesor */}
          {selectedAdvisor && (
            <AdvisorProfileModal
              advisor={selectedAdvisor}
              leads={leads}
              teamStats={teamStats}
              onClose={() => setSelectedAdvisor(null)}
            />
          )}
        </Sidebar>
      )}
    </ProtectedRoute>
  )
}

export default AdminPage
